
import { Component, Prop, Mixins, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import { UserPermissions, PaginatedSearchQueryParams } from "@/store/types";
import eventApi from "@/services/api.event.service";
import vSelect3 from "vselect3";
import { EventAlarmTag, EventAlarmTagRequest } from "@/types/sv-data/tags/EventAlarmTag";
import { AlarmTag } from '@/store/eventqueue/types';
import PaginatedSearch from '@/mixins/PaginatedSearch';
import { debounce } from 'lodash';
import { getDebouncePeriod } from '@/utils/debounce-helper';
import { truncateString } from "@/filters";
import { EventDetails } from "@/store/site-monitor/types";

const SiteMonitor = namespace("siteMonitor");

@Component({
	filters: { truncateString },
	components: {
		"v-select-3": vSelect3,
	},
})
export default class EventAlarmTags extends Mixins(PaginatedSearch) {
	@Prop(Number) readonly eventId: number;
	@Prop(Number) readonly groupId: number;
	@Prop({ type: Boolean, default: false }) readonly isPostEventEdit: boolean;

	@Getter("getPermissions") permissions: UserPermissions;
	@Getter getFeature: (featuresList: string[]) => boolean;

	@SiteMonitor.Getter("getEventDetails") eventDetails: EventDetails;

	private eventTags: EventAlarmTag[] = [];
	private selectedTags: AlarmTag[] = [];
	private isLoading: boolean = false;
	private loadingTagId: number | null = null;
	private debouncePeriod = 200;
	private alarmTagTitleTruncatedLength: number = 30;
	private allAvailableTags: AlarmTag[] = [];
	private defaultSearchParams: PaginatedSearchQueryParams = {
		page: 1,
		pageSize: 25,
		live: false,
		groupId: this.groupId,
	}

	private async mounted(): Promise<void> {
		await this.loadAllTagsAndEventTags();
	}

	private get isAlarmTagsEventReviewEnabled(): boolean {
		return this.getFeature(["Alarms", "AlarmTags", "EventReview"]);
	}

	private get canAddAlarmTagToEvent(): boolean {
		// Permission that allows user to add/remove alarm tags to/from an event
		var canAddAlarmTag = this.permissions.canAddAlarmTagToEvent;

		// Additional case for BUG 52088, which allows users to access the EventReview page for active events
		// This check ensures that post-event edits are only performed on closed events
		if (!(this.eventDetails && this.eventDetails.eventClosed == this.isPostEventEdit)) {
			return false;
		}

		// Can only add alarm tags to a closed event if the Alarms/AlarmTags/EventReview feature is enabled
		if (this.isPostEventEdit) {
			canAddAlarmTag = canAddAlarmTag && this.isAlarmTagsEventReviewEnabled;
		}

		return canAddAlarmTag;
	}

	@Watch("eventId")
	private async onEventIdChanged(newVal: number, oldVal: number): Promise<void> {
		if (newVal !== oldVal) {
			await this.loadAllTagsAndEventTags();			
		}
	}

	private async loadAllTagsAndEventTags(): Promise<void> {
		this.isLoading = true;
		try {
			await this.loadEventTags();
			await this.loadAvailableTags();
		}
		catch (error) {
			console.error("Failed to load tags:", error);
		}
		finally {
			this.isLoading = false;		
		}
	}

	private async loadEventTags(): Promise<void> {
		try {
			this.isLoading = true;
			const response = await eventApi.getAlarmTagsForEvent(this.eventId);
			this.eventTags = response.data.sort((a, b) => {
				return (a.removed === b.removed) ? 0 : a.removed ? 1 : -1;
			});
		} catch (error) {
			console.error("Failed to load event tags:", error);
			this.$notify({
				type: "error",
				title: "Error",
				text: "Failed to load event tags"
			});
		}
		finally {
			this.isLoading = false;
		}
	}

	private onSearch(searchTerm: string): void {
		this.handleSearchInput(searchTerm);
	}

	private handleSearchInput = debounce(async (searchTerm: string): Promise<void> => {
			await this.loadAvailableTags(searchTerm);
		},
		getDebouncePeriod(),
		{ leading: false, trailing: true }
	);

	private get availableTags(): AlarmTag[] {
		try {
			if (
				!Array.isArray(this.selectedTags) ||
				!Array.isArray(this.eventTags) ||
				!Array.isArray(this.allAvailableTags)
			) {
				return [];
			}
			const selectedIds = this.selectedTags.map(tag => tag?.responseAlarmTypeID).filter(id => id != null);
			const eventTagsIds = this.eventTags.map(tag => tag?.alarmTagId).filter(id => id != null);
			return this.allAvailableTags.filter(availableTag =>
				!selectedIds.includes(availableTag?.responseAlarmTypeID) &&
				!eventTagsIds.includes(availableTag?.responseAlarmTypeID)
			);
		} catch (e) {
			console.error("Error in getting available tags", e);
			this.$notify({
				type: "error",
				title: "Error",
				text: "Failed to load available tags"
			});
			return [];
		}
	}

	private async loadAvailableTags(searchTerm?: string): Promise<void> {
		try {
			const request = this.generateNewPaginatedSearchRequest({
				...this.defaultSearchParams,
				searchTerm: searchTerm || "",
			});
			const response = await eventApi.getEventAlarmTagsForArea(request);
			this.allAvailableTags = response.data;
		} catch (error) {
			console.error("Failed to load available tags:", error);
		}
	}

	private async addTags(): Promise<void> {
		if (!this.selectedTags || this.selectedTags.length === 0) {
			return;
		}

		try {
			const addPromises = this.selectedTags.map(tag => {
				const request: EventAlarmTagRequest = {
					eventId: this.eventId,
					alarmTagId: tag.responseAlarmTypeID,
				};

				return eventApi.addEventAlarmTag(request);
			});

			await Promise.all(addPromises);

			// Refresh tags
			await this.loadEventTags();
			await this.loadAvailableTags();

			// Reset selection
			this.selectedTags = [];
		} catch (error) {
			console.error("Failed to add tags:", error);
			this.$notify({
				type: "error",
				title: "Error",
				text: "Failed to add tags to event"
			});
		}
	}

	private debouncedTagStatus = debounce(async (tag: EventAlarmTag): Promise<void> => {
		if (tag.removed) {
			await this.restoreTag(tag.alarmTagId);
		} else {
			await this.removeTag(tag.alarmTagId);
		}
	}, this.debouncePeriod);

	// Wrapper around debounced function to handle loading state
	private handleTagStatus(tag: EventAlarmTag): void {
		if (!tag || !tag.alarmTagId) {
			console.error("Invalid tag provided to change status");
			this.$notify({
				type: "error",
				title: "Error",
				text: `Invalid tag provided to change status`
			});
		} else {
			this.loadingTagId = tag.alarmTagId; // Show spinner immediately
			this.debouncedTagStatus(tag);
		}
	}	

	private async removeTag(alarmTagId: number): Promise<void> {
		try {
			this.isLoading = true;
			const request: EventAlarmTagRequest = {
				eventId: this.eventId,
				alarmTagId: alarmTagId
			};

			await eventApi.removeEventAlarmTag(request);

			// Refresh tags
			await this.loadEventTags();
			await this.loadAvailableTags();
			
		} catch (error) {
			console.error(`Failed to remove tag ${alarmTagId}:`, error);
			this.$notify({
				type: "error",
				title: "Error",
				text: `Failed to remove tag (ID: ${alarmTagId}) from event`
			});
		}
		finally {
			this.isLoading = false;
			// Set loading state to null to hide spinner
			this.loadingTagId = null;
		}
	}

	private async restoreTag(alarmTagId: number): Promise<void> {
		try {
			this.isLoading = true;
			const request: EventAlarmTagRequest = {
				eventId: this.eventId,
				alarmTagId: alarmTagId
			};

			await eventApi.addEventAlarmTag(request);

			// Refresh tags
			await this.loadEventTags();
			await this.loadAvailableTags();
			
		} catch (error) {
			console.error(`Failed to restore tag ${alarmTagId}:`, error);
			this.$notify({
				type: "error",
				title: "Error",
				text: `Failed to restore tag (ID: ${alarmTagId}) from event`
			});
		}
		finally {
			this.isLoading = false;
			// Set loading state to null to hide spinner
			this.loadingTagId = null;
		}
	}
}
